import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = {
  key: 0,
  class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap"
}
const _hoisted_3 = { class: "d-flex align-items-center gap-2" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "pagination-mini" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled", "onClick"]
const _hoisted_8 = ["disabled"]

import { ref, computed, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud"; // Adjust path
import BaseModal from "@/components/commen/modal/BaseModal.vue"; // Adjust path
import BaseForm from "@/components/commen/modal/BaseForm.vue"; // Adjust path
// import zeroConfiguration from "@/components/commen/table/zeroConfiguration.vue";
import { useRouter } from "vue-router";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";

const API_RESOURCE = "/common/prompts";

export default /*@__PURE__*/_defineComponent({
  __name: 'promptList',
  setup(__props) {

const { canCreate } = usePermissions();

const router = useRouter();
const crud = useCrud(API_RESOURCE);

const isModalOpen = ref(false);
const isViewMode = ref(false);
const isEditMode = ref(false);
const formData = ref({
  title: "",
  target: "",
  prompt: "",
  variables: [],
  isReusable: false,
});

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title" },
    { name: "Category", field: "prompt_category_title" },
    { name: "Base Prompt", field: "base_prompt_title" },
  ],
  data: crud.data.value,
  actions: [true],
  loading: crud.isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newData) => {
  tableConfig.value.data = newData;
});

watch(crud.isLoading, (val) => {
  tableConfig.value.loading = val;
});

watch(crud.pagination, (newPagination) => {
  if (newPagination && Object.keys(newPagination).length > 0) {
    tablePagination.value.current_page = newPagination.current_page || newPagination.currentPage || newPagination.page || 1;
    tablePagination.value.total_pages = newPagination.total_pages || newPagination.totalPages || Math.ceil((newPagination.total || newPagination.totalItems || 0) / tablePagination.value.per_page) || 1;
    tablePagination.value.total = newPagination.total || newPagination.totalItems || 0;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
  }
}, { deep: true });

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (typeof page !== "number" || Number.isNaN(page)) return;
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) {
    return;
  }
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const createPrompt = () => router.push("/prompt-create");

const formFields = computed(() => [
  { key: "title", label: "Title", type: "text", required: true },
  {
    key: "target",
    label: "Target",
    type: "select",
    required: true,
    options: [
      { title: "Workspace", value: "workspace" },
      { title: "Lane", value: "lane" },
      { title: "Module", value: "module" },
      { title: "Sheet", value: "sheet" },
      { title: "CardList", value: "cardlist" },
      { title: "Card", value: "card" },
      { title: "Shared", value: "shared" },
    ],
  },
  {
    key: "prompt",
    label: "Prompt",
    type: "textarea",
    required: true,
    placeholder: "Use {placeholders}",
  },
  {
    key: "variables",
    label: "Variables",
    type: "chip",
    placeholder: "Enter variables (e.g. workspace-title)",
  },
  { key: "isReusable", label: "Reusable Snippet", type: "checkbox" },
]);

const isFormInvalid = computed(
  () => !formData.value.title?.trim() || !formData.value.prompt?.trim()
);

function openModal(mode: "create" | "edit" | "view", data: any = null) {
  isModalOpen.value = true;
  isViewMode.value = mode === "view";
  isEditMode.value = mode === "edit";
  formData.value = data
    ? { ...data, variables: data.variables || [] }
    : { title: "", target: "", prompt: "", variables: [], isReusable: false };
}

function closeModal() {
  isModalOpen.value = false;
  isViewMode.value = false;
  isEditMode.value = false;
  formData.value = {
    title: "",
    target: "",
    prompt: "",
    variables: [],
    isReusable: false,
  };
}

const handleEdit = (row: any) =>
  router.push({ path: "/prompt-edit", query: { id: row._id } });
const handleView = (row: any) =>
  router.push({ path: "/prompt-edit", query: { id: row._id, view: "1" } });

async function handleDelete(id: string) {
  if (confirm("You can't delete this prompt it is in use")) {
    //await crud.deleteItem(id);
    //await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
}

async function handleSave() {
  if (isFormInvalid.value) return;
  const { _id, ...data } = formData.value;
  const result = _id
    ? await crud.updateItem(_id, data)
    : await crud.createItem(data);
  if (result) {
    closeModal();
    if (_id) {
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    } else {
      await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
    }
  }
}

crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });

return (_ctx: any,_cache: any) => {
  const _component_Breadcrumbs = _resolveComponent("Breadcrumbs")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Breadcrumbs, {
      title: 'Prompt Templates',
      buttonLabel: 'Create Prompt',
      onButtonClick: createPrompt,
      disabled: !_unref(canCreate)('main')
    }, null, 8, ["disabled"]),
    _createVNode(CommonTable2, {
      tableObj: tableConfig.value,
      title: 'Prompt Templates',
      permissionKey: "main",
      onEdit: handleEdit,
      onDelete: handleDelete,
      onView: handleView
    }, null, 8, ["tableObj"]),
    (!_unref(crud).isLoading && _unref(crud).data.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select form-select-sm rows-select",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
              onChange: changeRows
            }, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
                return _createElementVNode("option", {
                  key: n,
                  value: n
                }, _toDisplayString(n), 9, _hoisted_4)
              }), 64))
            ], 544), [
              [
                _vModelSelect,
                tablePagination.value.per_page,
                void 0,
                { number: true }
              ]
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              class: "page-btn prev_btn",
              disabled: !tablePagination.value.has_prev,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
            }, " « ", 8, _hoisted_6),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: index,
                class: _normalizeClass(["page-btn", {
            'page-active': p === tablePagination.value.current_page,
            'disabled-dots': p === '...',
          }]),
                disabled: p === '...',
                onClick: ($event: any) => (typeof p === 'number' && handlePageChange(p))
              }, _toDisplayString(p), 11, _hoisted_7))
            }), 128)),
            _createElementVNode("button", {
              class: "page-btn next_btn",
              disabled: !tablePagination.value.has_next,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
            }, " » ", 8, _hoisted_8)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})